import { useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { MappedFilterResult, MappedFilterValue } from 'types';
import { isMappedFilterValue, isThreeLevelsFilterValue } from 'types';

interface UseNormalizeFilterValuesProps {
  mappedFilterValues: MappedFilterResult[];
}

export const useNormalizeFilterValues = ({ mappedFilterValues }: UseNormalizeFilterValuesProps) => {
  const hasShownWarning = useRef(false);
  const { t } = useTranslation();

  const normalizedFilters = useMemo(() => {
    const hasThirdLevelFilters = mappedFilterValues.some((filter) => isThreeLevelsFilterValue(filter) && filter.secondLevelNestedFormItemId);

    if (hasThirdLevelFilters && !hasShownWarning.current) {
      toast.warning(t('filters.nestedLevelWarning'));
      hasShownWarning.current = true;
    }

    return mappedFilterValues
      .filter((filter) => {
        if (isThreeLevelsFilterValue(filter)) {
          return !filter.secondLevelNestedFormItemId;
        }
        return true;
      })
      .map((filter): MappedFilterValue => {
        if (isThreeLevelsFilterValue(filter)) {
          return {
            formItemId: filter.mainFormItemId,
            formItemType: filter.formItemType ?? '',
            referencedFormItemId: filter.nestedFormItemId,
            values: filter.values,
            value: filter.value,
            fromValue: filter.fromValue,
            toValue: filter.toValue,
          };
        }

        if (isMappedFilterValue(filter) && filter.formItemType) {
          return filter;
        }

        return {
          formItemId: filter.formItemId,
          formItemType: filter.formItemType ?? '',
          referencedFormItemId: filter.referencedFormItemId,
          values: filter.values,
          value: filter.value,
          fromValue: filter.fromValue,
          toValue: filter.toValue,
        };
      });
  }, [mappedFilterValues, t]);

  return { normalizedFilters };
};
